import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { RootState } from "../../../../../store/store";
import { getClaimsData } from "../../../../Dashboard/DashboardAction";
import { IClaimId } from "../../../../../models/user/policyDetails/PolicyDetails";
import { IClaim } from "../../../../../models/user/dashboard/Dashboard";
import { GetPolicyClaimsTransactions } from "../../../../../store/user/policyDetails/policyDetailsActions";
import { Timeline, timelineOppositeContentClasses } from "@mui/lab";
import CustomTimeline from "../../../../../common/CustomTimeline/CustomTimeline";
import moment from "moment";
import { dateFormat } from "../../../../../constants/Constants";
import { Box, Typography, useMediaQuery } from "@mui/material";
import ClaimActivityCard from "./ClaimActivityCard/ClaimActivityCard";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "../../../../../common/CustomSkeleton/CustomSkeleton";
import { ActivityStyles } from "../ActivityStyles";

const Claims = () => {
  const dispatch = useAppDispatch();
  const { carrier, system, lobCode } = useParams();
  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { claimsData } = useAppSelector((state: RootState) => state.dashboard);
  const { policyDetailsHeader, claimsTransactions } = useAppSelector(
    (state: RootState) => state.policyDetails
  );
  const { calledReducerType, isLoading } = useAppSelector(
    (state: RootState) => state.common
  );

  const { t } = useTranslation();

  const isMobile = useMediaQuery("(max-width:1024px)");

  useEffect(() => {
    if (userDetails?.customerId) {
      dispatch(
        getClaimsData({
          customerId: userDetails.customerId,
          carrier: "PRIS",
          system: "INOW",
        })
      );
    }
  }, [userDetails?.customerId, dispatch]);

  useEffect(() => {
    if (
      policyDetailsHeader &&
      carrier &&
      system &&
      lobCode &&
      policyDetailsHeader.carrierPolicyNumber &&
      claimsData &&
      claimsData.claimList &&
      claimsData.claimList.length > 0
    ) {
      const claimIds: IClaimId[] = claimsData.claimList
        .filter(
          (claim: IClaim) =>
            claim.carrierPolicyNo === policyDetailsHeader.carrierPolicyNumber
        )
        .map((claim: IClaim) => ({
          claimId: claim.claimId,
          carrier,
          system,
          lobCode,
        }));
      dispatch(GetPolicyClaimsTransactions(claimIds));
    }
  }, [
    claimsData,
    policyDetailsHeader,
    policyDetailsHeader?.carrierPolicyNumber,
    dispatch,
    carrier,
    system,
    lobCode,
  ]);

  return isLoading && calledReducerType === "dashboard/getClaimsData" ? (
    <CustomSkeleton count={3} direction="column" height="120px" />
  ) : claimsTransactions.length > 0 ? (
    <Timeline
      id="claims_activity-timeline"
      data-test-id="test-claims_activity-timeline"
      aria-label="Claims Activity Timeline"
      sx={{
        padding: "0px",
        [`& .${timelineOppositeContentClasses.root}`]: { flex: 0.1 },
      }}
    >
      {[...claimsTransactions]
        .sort(
          (a, b) =>
            new Date(b.transactionDate).getTime() -
            new Date(a.transactionDate).getTime()
        )
        .map((transaction, index) => (
          <CustomTimeline
            key={`policy-activity-${index}`}
            oppositeContent={moment(transaction.transactionDate).format(
              dateFormat
            )}
            oppositeContentStyle={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            dotStyle={{
              display: "flex",
              alignItems: "center",
            }}
            children={
              <ClaimActivityCard
                transaction={transaction}
                isLast={
                  index ===
                  (claimsTransactions
                    ? isMobile
                      ? claimsTransactions.length - 2
                      : claimsTransactions.length - 1
                    : false)
                }
              />
            }
          />
        ))}
    </Timeline>
  ) : (
    <Box 
      id="claims_activity-no-data"
      data-test-id="test-claims_activity-no-data"
      aria-label="No Claims Data Available"
      sx={ActivityStyles.noDataAvailable}
    >
      <Typography 
        id="claims_activity-no-data-text"
        data-test-id="test-claims_activity-no-data-text"
        aria-label="No Claims Data Text"
        variant="h6" 
        fontSize="1rem"
      >
        {t("No Claims Available")}
      </Typography>
    </Box>
  );
};

export default Claims;
